/**
 * Application Store -- Zustand state management for the application form wizard.
 *
 * Manages:
 *   - Current application data (loaded from backend, cached in store)
 *   - Wizard step progression (steps 1-8: type -> personal -> employment -> etc.)
 *   - Draft auto-save (debounced PUT to /applications/{id}/draft)
 *   - Pre-creation wizard draft (persisted to localStorage before applicationId exists)
 *   - Optimistic updates (UI updates immediately, rolls back on API failure)
 *   - Cache invalidation (refetch on step change, bank selection, etc.)
 *   - Bank account data (loaded from separate bankAccountService)
 *
 * The store is the single source of truth for the form wizard UI. Components
 * read from the store; mutations go through store actions which call the API
 * and update local state on success.
 */
import { create } from 'zustand';
import applicationService from '../services/applicationService';
import bankAccountService, { CustomerBankAccountResponse } from '../services/bankAccountService';
import { useDelegationStore } from './delegationStore';

// ── Wizard Draft Persistence ──
// Steps 1-2 happen before the application exists on the backend.
// We persist this pre-creation state to localStorage so it survives a same-user
// re-login (e.g. token expiry). The draft is scoped to the user who created it
// (ownerUserId) so a different account on the same browser can never inherit it.
const WIZARD_DRAFT_KEY = 'hipotech_wizard_draft';

export interface WizardDraft {
    customerType: CustomerType;
    country: string;
    productType?: ProductType;
    currentStep: number;
    savedAt: string;
    /** User id of the account that created this draft (owner scoping). */
    ownerUserId?: string;
}

/**
 * Current authenticated user id, read from the persisted auth store
 * (localStorage 'auth-storage'). Returns null when no user is signed in.
 * Read directly (rather than importing the auth store) to avoid a store
 * import cycle, matching the apiClient delegation guard.
 */
function currentAuthUserId(): string | null {
    try {
        const raw = localStorage.getItem('auth-storage');
        if (!raw) return null;
        return JSON.parse(raw)?.state?.user?.id ?? null;
    } catch {
        return null;
    }
}

export function saveWizardDraft(draft: Partial<WizardDraft>) {
    // Don't save wizard drafts during delegation — the app belongs to someone else
    if (useDelegationStore.getState().isActive) return;
    try {
        const existing = loadWizardDraft();
        const merged = {
            ...existing,
            ...draft,
            // Stamp the current user as the owner on every save. Every writer of
            // this key runs while authenticated (Step 1 is auth-gated; the advisor
            // modal only saves on its isAuthenticated branch), so this is always set.
            ownerUserId: currentAuthUserId() ?? undefined,
            savedAt: new Date().toISOString(),
        };
        localStorage.setItem(WIZARD_DRAFT_KEY, JSON.stringify(merged));
    } catch { /* localStorage unavailable */ }
}

export function loadWizardDraft(): WizardDraft | null {
    try {
        const raw = localStorage.getItem(WIZARD_DRAFT_KEY);
        if (!raw) return null;
        const draft = JSON.parse(raw) as WizardDraft;
        // Expire after 24 hours
        if (draft.savedAt) {
            const age = Date.now() - new Date(draft.savedAt).getTime();
            if (age > 24 * 60 * 60 * 1000) {
                clearWizardDraft();
                return null;
            }
        }
        // Owner scoping: only surface a draft to the user who created it. This
        // stops a freshly-created (or any other) account on a shared browser from
        // inheriting a phantom "continue your application" from a previous session.
        // A pre-scoping draft (no ownerUserId) is treated as unowned and is never
        // shown to a signed-in user.
        if ((draft.ownerUserId ?? null) !== currentAuthUserId()) {
            return null;
        }
        return draft.customerType ? draft : null;
    } catch {
        return null;
    }
}

export function clearWizardDraft() {
    try {
        localStorage.removeItem(WIZARD_DRAFT_KEY);
    } catch { /* localStorage unavailable */ }
}

// Debounce utility
function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

export type CustomerType = 'NATIONAL_CITIZEN' | 'FOREIGN_RESIDENT' | 'FOREIGN_NON_RESIDENT' | 'CORPORATE_INVESTOR' | 'NOT_APPLICABLE';
export type ProductType = 'PERSONAL_PREAPPROVAL' | 'PROPERTY_PREAPPROVAL' | 'FULL_APPLICATION' | 'EQUITY_MORTGAGE';
export type OwnershipStatus = 'OWNED_FREE_CLEAR' | 'OWNED_WITH_MORTGAGE';
export type UseOfFunds = 'DEBT_CONSOLIDATION' | 'HOME_IMPROVEMENT' | 'INVESTMENT' | 'BUSINESS' | 'PAY_PROPERTY_OBLIGATIONS' | 'OTHER';

export interface ConsolidationDebt {
    creditorName: string;
    balance: number;
    monthlyPayment: number;
    accountRef?: string;
}
export type CreditAssessmentStatus = 'not_applicable' | 'not_submitted' | 'pending_review' | 'passed' | 'failed' | 'manual_review';
export type ApplicationStatus = 'DRAFT' | 'PAYMENT_CONFIRMED' | 'READY_FOR_SUBMISSION' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';

export interface ApplicationData {
    id?: string;
    applicationId?: string; // Server-generated ID like "HT-2025-12-00234"
    customerId?: string;   // Owner's user ID — used to guard owner-only UI
    status: ApplicationStatus;
    currentStep: number;

    // Step 1: Customer Type
    customerType?: CustomerType;

    // Step 2: Product Type
    productType?: ProductType;
    preConstruction?: boolean;
    preConstructionProjectName?: string;
    parentApplicationId?: string;

    // Country for form schema (defaults to 'DO')
    country?: string;

    // Eligible banks for this application
    eligibleBanks?: Array<{ bankId: string; bankName: string; logoUrl: string }>;
    eligibleBankCount?: number;
    banksAnonymized?: boolean;
    blockingCoApplicants?: Array<{
        participantId: string;
        firstName: string | null;
        lastName: string | null;
        email: string | null;
        role: string | null;
        status: string;
    }>;

    // Step 3: Personal Info
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string;
    idNumber?: string;
    nationality?: string;
    countryOfResidence?: string;
    currentAddress?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
    maritalStatus?: 'SINGLE' | 'MARRIED' | 'DIVORCED' | 'WIDOWED';
    numberOfDependents?: number;

    // Step 4: Financial Info
    employmentStatus?: string;
    employerName?: string;
    jobTitle?: string;
    yearsEmployed?: number;
    employmentStartDate?: string;
    // ── Financial ───────────────────────────────────────────────────────────────
    // ALL monetary values below are stored and received in DOP (STORAGE_CURRENCY).
    // To display in the user's incomeCurrency: divide by exchangeRateToDOP.
    // To save to the backend: multiply by exchangeRateToDOP to convert from
    // whatever currency the user typed.
    monthlyIncome?: number;
    annualIncome?: number;
    otherIncome?: number;
    monthlyExpenses?: number;
    existingDebts?: number;
    totalAssets?: number;

    // Currency support
    incomeCurrency?: string; // ISO 4217 currency code (default: 'DOP')

    // Converted DOP values (from API response)
    annualIncomeInDOP?: number;
    monthlyIncomeInDOP?: number;
    propertyValueInDOP?: number;
    loanAmountInDOP?: number;
    exchangeRateToDOP?: number;

    // Step 5: Property Info
    propertyAddress?: string;
    propertyType?: string;
    propertyCondition?: string;
    occupancyType?: string;
    loanPurpose?: string;
    propertyValue?: number;
    loanAmount?: number;
    downPayment?: number;
    loanTerm?: number;
    province?: string;
    municipality?: string;
    ltv?: number;

    // Equity Mortgage fields
    ownershipStatus?: OwnershipStatus;
    outstandingObligations?: number;
    outstandingObligationsDescription?: string;
    existingMortgageBalance?: number;
    existingMortgageLender?: string;
    requestedLoanAmount?: number;
    useOfFunds?: UseOfFunds;
    rentalIncome?: number;
    rentalDocumentation?: boolean;
    propertyAcquiredDate?: string;
    existingAppraisal?: boolean;
    consolidationDebts?: ConsolidationDebt[];

    // Step 5.5: Appraiser Selection
    appraiserId?: string;              // DB appraiser ID (relational)
    appraiserName?: string;            // DB appraiser display name
    appraiserSource?: 'DB' | 'EXTERNAL'; // how the appraiser was captured
    appraiserExternalName?: string;    // free-text name when not in DB
    appraiserExternalPhone?: string;   // phone number (external)
    appraiserLicenseNumber?: string;   // BNV license number (external)
    appraiserExternalBanks?: string[]; // bank IDs the external appraiser claims

    // Step 6: Documents
    uploadedDocuments?: string[];

    // Bank Accounts (Per-Bank Relationships)
    bankAccounts?: CustomerBankAccountResponse[];

    // Credit Assessment
    creditAssessmentStatus?: CreditAssessmentStatus;
    creditScore?: number;
    creditBureau?: string;
    creditReportDate?: string;
    creditReportDocumentId?: string;
    creditWaiverReason?: string;

    // Premium Submission
    isPremiumSubmission?: boolean;
    premiumExplanation?: string;

    // Payment
    paymentStatus?: 'pending' | 'completed' | 'failed';
    paymentAmount?: number;

    // Timestamps
    createdAt?: string;
    updatedAt?: string;
    submittedAt?: string;
}

interface ApplicationState {
    application: ApplicationData;
    autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error';
    isInitialized: boolean;

    // Actions
    initializeApplication: () => Promise<void>;
    /** Load an existing application by ID — single source of truth for API→store rehydration. */
    loadApplicationById: (applicationId: string) => Promise<void>;
    createNewApplication: (customerType: CustomerType, productType: ProductType, country?: string, preConstruction?: boolean, preConstructionProjectName?: string) => Promise<string | null>;
    updateApplication: (data: Partial<ApplicationData>) => void;
    /** Like updateApplication but allows null values through — use after server-authoritative
     *  operations (e.g. product type conversion) where null means "field was cleared". */
    mergeApplicationFromServer: (data: Record<string, unknown>) => void;
    saveToServer: () => Promise<void>;
    setCurrentStep: (step: number) => void;
    setAutoSaveStatus: (status: 'idle' | 'saving' | 'saved' | 'error') => void;
    resetApplication: () => void;

    // Bank Account Actions
    loadBankAccounts: () => Promise<void>;
    addBankAccount: (account: CustomerBankAccountResponse) => void;
    updateBankAccount: (accountId: number, account: CustomerBankAccountResponse) => void;
    removeBankAccount: (accountId: number) => void;

    // Helper methods for status checks
    canEditApplication: () => boolean;
    canSubmitSecure: () => boolean;
    isSubmitted: () => boolean;
}

const initialApplication: ApplicationData = {
    status: 'DRAFT',
    currentStep: 1,
    paymentStatus: 'pending',
    creditAssessmentStatus: 'not_submitted',
    incomeCurrency: 'DOP',
};

// Helper function to filter out placeholder values
const filterPlaceholder = (value: any, placeholders: any[]): any => {
    if (value === undefined || value === null) return undefined;
    if (placeholders.includes(value)) return undefined;
    return value;
};

/**
 * Single source of truth for mapping an API response to ApplicationData.
 * Used by both initializeApplication (new/draft) and loadApplicationById (resume).
 * No other code path should duplicate this mapping.
 */
function hydrateApplicationFromApi(apiData: any): ApplicationData {
    const stringPlaceholders = ['TBD', 'placeholder@example.com', '+00000000000', '+18095550100', 'not_specified', '2000-01-01'];

    // Helper: read a field from flat camelCase response OR nested snake_case fallback
    const f = (camel: string, nestedPath?: string) => {
        const val = (apiData as any)[camel];
        if (val !== undefined && val !== null) return val;
        if (nestedPath) {
            const [group, field] = nestedPath.split('.');
            return (apiData as any)[group]?.[field];
        }
        return undefined;
    };

    const rawFirstName = f('firstName', 'personal_info.first_name');
    const isFreshDraft = rawFirstName === 'TBD' || !rawFirstName;

    // Helper: detect draft-creation financial placeholders.
    // applicationStore sends 1000 DOP for annualIncome/loanAmountRequested
    // to pass API validation. Currency round-trips (DOP→USD→DOP) cause
    // drift (~999.85), so use a threshold. No real mortgage value is ≤1100 DOP.
    const isFinancialPlaceholder = (v: any): boolean =>
        typeof v === 'number' && v > 0 && v <= 1100;

    return {
        ...initialApplication,
        id: f('applicationId', 'application_id') || apiData.application_id,
        applicationId: f('applicationId', 'application_id') || apiData.application_id,
        customerId: f('customerId') as string | undefined,
        currentStep: (f('currentStep') as number) || 1,
        status: ((f('currentStatus') || apiData.current_status)?.toUpperCase() as ApplicationStatus) || 'DRAFT',
        customerType: (apiData.customerType ?? apiData.customer_type ?? null) as CustomerType,
        productType: (apiData.productType ?? apiData.product_type ?? null) as ProductType,
        parentApplicationId: f('parentApplicationId') || undefined,
        preConstruction: f('preConstruction') === true || f('preConstruction') === 'true' || false,
        preConstructionProjectName: f('preConstructionProjectName') || undefined,
        occupancyType: f('occupancyType') as string | undefined,

        // Personal info
        firstName: filterPlaceholder(f('firstName', 'personal_info.first_name'), stringPlaceholders) as string | undefined,
        lastName: filterPlaceholder(f('lastName', 'personal_info.last_name'), stringPlaceholders) as string | undefined,
        email: filterPlaceholder(f('email', 'personal_info.email'), stringPlaceholders) as string | undefined,
        phone: filterPlaceholder(f('phoneNumber', 'personal_info.phone'), stringPlaceholders) as string | undefined,
        dateOfBirth: filterPlaceholder(f('dateOfBirth', 'personal_info.date_of_birth'), stringPlaceholders) as string | undefined,
        nationality: f('nationality') as string | undefined,
        maritalStatus: f('maritalStatus') as string | undefined,
        numberOfDependents: f('numberOfDependents') as number | undefined,
        idNumber: filterPlaceholder(f('passportNumber') || f('cedula'), stringPlaceholders) as string | undefined,
        currentAddress: filterPlaceholder(f('currentAddress'), stringPlaceholders) as string | undefined,
        city: filterPlaceholder(f('city'), stringPlaceholders) as string | undefined,
        stateProvince: f('stateProvince') as string | undefined,
        postalCode: f('postalCode') as string | undefined,
        countryOfResidence: f('residenceCountry') || f('countryOfResidence') || f('country') || undefined,

        // Employment & Financial
        employmentStatus: (() => {
            const v = filterPlaceholder(f('employmentStatus', 'financial_info.employment_status'), stringPlaceholders) as string | undefined;
            // Fresh drafts send EMPLOYED as placeholder — clear it
            return (isFreshDraft && v === 'EMPLOYED') ? undefined : v;
        })(),
        employerName: f('employerName', 'financial_info.employer_name') as string | undefined,
        jobTitle: f('jobTitle') as string | undefined,
        yearsEmployed: f('yearsEmployed') as number | undefined,
        employmentStartDate: f('employmentStartDate') as string | undefined,
        incomeCurrency: (f('incomeCurrency', 'financial_info.income_currency') || apiData.income_currency) as string | undefined,
        monthlyIncome: (() => {
            const v = f('monthlyIncome', 'financial_info.monthly_income') as number | undefined;
            return (isFreshDraft && isFinancialPlaceholder(v)) ? undefined : v;
        })(),
        annualIncome: (() => {
            const v = f('annualIncome', 'financial_info.annual_income') as number | undefined;
            return (isFreshDraft && isFinancialPlaceholder(v)) ? undefined : v;
        })(),
        otherIncome: f('otherIncome') as number | undefined,
        monthlyExpenses: f('monthlyDebtPayments') || f('monthlyExpenses') as number | undefined,
        existingDebts: f('totalLiabilities') as number | undefined,
        totalAssets: f('totalAssets') as number | undefined,
        creditScore: f('creditScore') as number | undefined,
        exchangeRateToDOP: f('exchangeRateToDOP') as number | undefined,

        // Property
        propertyAddress: filterPlaceholder(f('propertyAddress', 'property_info.property_address'), stringPlaceholders) as string | undefined,
        propertyType: f('propertyType', 'property_info.property_type') as string | undefined,
        propertyCondition: f('propertyCondition', 'property_info.property_condition') as string | undefined,
        propertyValue: (() => {
            const v = f('propertyValue', 'property_info.property_value') as number | undefined;
            return (isFreshDraft && isFinancialPlaceholder(v)) ? undefined : v;
        })(),
        loanAmount: (() => {
            const v = (f('loanAmountRequested') || f('loanAmount', 'property_info.loan_amount')) as number | undefined;
            return (isFreshDraft && isFinancialPlaceholder(v)) ? undefined : v;
        })(),
        downPayment: f('downPayment', 'property_info.down_payment') as number | undefined,
        loanTerm: (f('askedLoanTermYears') || f('loanTerm', 'property_info.loan_term') || (apiData as any)?.askedLoanTermYears) as number | undefined,
        province: (f('province', 'property_info.province') || f('propertyProvince')) as string | undefined,
        municipality: (f('municipality', 'property_info.municipality') || f('propertyCity')) as string | undefined,

        // Equity mortgage fields
        propertyAcquiredDate: f('propertyAcquiredDate', 'property_info.property_acquired_date') as string | undefined,
        existingAppraisal: f('existingAppraisal', 'property_info.existing_appraisal') as boolean | undefined,
        ownershipStatus: f('ownershipStatus', 'property_info.ownership_status') as OwnershipStatus | undefined,
        outstandingObligations: f('outstandingObligations', 'property_info.outstanding_obligations') as number | undefined,
        outstandingObligationsDescription: f('outstandingObligationsDescription', 'property_info.outstanding_obligations_description') as string | undefined,
        existingMortgageBalance: f('existingMortgageBalance', 'property_info.existing_mortgage_balance') as number | undefined,
        existingMortgageLender: f('existingMortgageLender', 'property_info.existing_mortgage_lender') as string | undefined,
        requestedLoanAmount: f('requestedLoanAmount', 'property_info.requested_loan_amount') as number | undefined,
        useOfFunds: f('useOfFunds', 'property_info.use_of_funds') as UseOfFunds | undefined,
        rentalIncome: f('rentalIncome', 'property_info.rental_income') as number | undefined,
        rentalDocumentation: f('rentalDocumentation', 'property_info.rental_documentation') as boolean | undefined,
        consolidationDebts: (() => {
            const raw = f('consolidationDebtsJson') || f('consolidationDebts', 'property_info.consolidation_debts_json');
            if (!raw) return undefined;
            if (Array.isArray(raw)) return raw as ConsolidationDebt[];
            if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return undefined; } }
            return undefined;
        })(),

        // Other
        loanPurpose: f('loanPurpose', 'property_info.loan_purpose') as string | undefined,
        ltv: f('ltv') as number | undefined,
        creditAssessmentStatus: f('creditAssessmentStatus') as string | undefined,

        // Pre-construction
        developerName: f('developerName') as string | undefined,
        expectedCompletionDate: f('expectedCompletionDate') as string | undefined,
        contractSigned: f('contractSigned') as boolean | undefined,
        firstPaymentMade: f('firstPaymentMade') as boolean | undefined,
        instalmentFrequency: f('instalmentFrequency') as string | undefined,

        // Appraiser
        appraiserId: f('appraiserId') as string | undefined,
        appraiserSource: f('appraiserSource') as string | undefined,
        appraiserName: f('appraiserName') as string | undefined,
        appraiserExternalName: f('appraiserExternalName') || (apiData as any).appraiser_external_name || undefined,
        appraiserExternalPhone: f('appraiserExternalPhone') || (apiData as any).appraiser_external_phone || undefined,
        appraiserLicenseNumber: f('appraiserLicenseNumber') || (apiData as any).appraiser_license_number || undefined,
        appraiserExternalBanks: f('appraiserExternalBanks') || (apiData as any).appraiser_external_banks || undefined,

        createdAt: f('createdAt') || apiData.created_at,
        updatedAt: f('updatedAt') || apiData.updated_at,
    };
}

export const useApplicationStore = create<ApplicationState>((set, get) => {
    // Debounced save function (2 seconds delay)
    const debouncedSave = debounce(async () => {
        await get().saveToServer();
    }, 2000);

    return {
        application: initialApplication,
        autoSaveStatus: 'idle',
        isInitialized: false,

        /**
         * Initialize - Load existing draft or start fresh
         */
        initializeApplication: async () => {
            try {
                set({ autoSaveStatus: 'saving' });

                const response = await applicationService.getDraftApplication();

                if (response.success && response.data) {
                    let apiData = response.data;

                    const appId = (apiData as any).applicationId || apiData.application_id;
                    if (appId) {
                        try {
                            const detailResponse = await applicationService.getApplication(appId);
                            if (detailResponse.success && detailResponse.data) {
                                apiData = detailResponse.data;
                            }
                        } catch (err) {
                            console.warn('Failed to fetch full application details, using summary:', err);
                        }
                    }

                    set({
                        application: hydrateApplicationFromApi(apiData),
                        autoSaveStatus: 'saved',
                        isInitialized: true,
                    });
                } else {
                    // No existing draft — start with local state only.
                    // A server-side draft (skeleton) is created on demand when needed
                    // (e.g. when the user clicks "Invite someone to help").
                    set({
                        application: initialApplication,
                        autoSaveStatus: 'idle',
                        isInitialized: true,
                    });
                }
            } catch (error) {
                console.error('Failed to initialize application:', error);
                set({
                    application: initialApplication,
                    autoSaveStatus: 'error',
                    isInitialized: true,
                });
            }
        },

        loadApplicationById: async (applicationId: string) => {
            try {
                const response = await applicationService.getApplication(applicationId);
                console.log(`[DELEG-DIAG] loadApplicationById: appId=${applicationId}  response.success=${response?.success}  response.data.customerType=${response?.data?.customerType}  response.data.customer_type=${response?.data?.customer_type}  response.data.productType=${response?.data?.productType}`);
                if (response.success && response.data) {
                    const hydrated = hydrateApplicationFromApi(response.data);
                    console.log(`[DELEG-DIAG] loadApplicationById: hydrated customerType=${hydrated.customerType}  productType=${hydrated.productType}  appId=${hydrated.applicationId}`);
                    set({
                        application: hydrated,
                        autoSaveStatus: 'idle',
                        isInitialized: true,
                    });
                } else {
                    set({ autoSaveStatus: 'idle', isInitialized: true });
                }
            } catch (error) {
                console.error('Failed to load application:', error);
                set({ autoSaveStatus: 'idle', isInitialized: true });
            }
        },

        /**
         * Create new application on server
         */
        createNewApplication: async (customerType, productType, country, preConstruction, preConstructionProjectName) => {
            try {
                set({ autoSaveStatus: 'saving' });

                // If a skeleton draft already exists (from initDraft), update it instead of creating a new one
                const existingId = get().application.applicationId || get().application.id;
                if (existingId) {
                    const updateData: Record<string, unknown> = {
                        customerType,
                        productType,
                        ...(preConstruction ? { preConstruction: true } : {}),
                        ...(preConstructionProjectName ? { preConstructionProjectName } : {}),
                    };

                    await applicationService.updateApplication(existingId, updateData);

                    set({
                        application: {
                            ...get().application,
                            customerType,
                            productType,
                            preConstruction: preConstruction || false,
                            preConstructionProjectName: preConstructionProjectName || undefined,
                        },
                        autoSaveStatus: 'saved',
                    });
                    return existingId;
                }

                // Use provided country or default to DO for national citizens
                const countryValue = country || (customerType === 'NATIONAL_CITIZEN' ? 'DO' : 'DO');

                // Send ALL required fields with placeholders to satisfy API validation
                // API expects camelCase field names
                const response = await applicationService.createApplication({
                    customerType: customerType,
                    productType: productType,
                    // Pre-construction flag + project name (optional)
                    ...(preConstruction ? { preConstruction: true } : {}),
                    ...(preConstructionProjectName ? { preConstructionProjectName } : {}),
                    // Required financial field
                    incomeCurrency: 'DOP',
                    // Personal fields (placeholders for draft)
                    firstName: 'TBD',
                    lastName: 'TBD',
                    email: 'placeholder@example.com',
                    dateOfBirth: '2000-01-01',
                    nationality: 'DO',
                    passportNumber: 'TBD',
                    // Address fields
                    currentAddress: 'TBD',
                    city: 'TBD',
                    country: countryValue,
                    // Financial fields
                    employmentStatus: 'EMPLOYED',
                    annualIncome: 1000,
                    // Loan field
                    loanAmountRequested: 1000,
                });

                if (response.success) {
                    // API returns applicationId in camelCase
                    const newAppId = (response.data as any).applicationId;

                    set({
                        application: {
                            ...initialApplication,
                            id: newAppId,
                            applicationId: newAppId,
                            customerType,
                            productType,
                            preConstruction: preConstruction || false,
                            preConstructionProjectName: preConstructionProjectName || undefined,
                            status: 'DRAFT',
                            createdAt: response.data.created_at,
                        },
                        autoSaveStatus: 'saved',
                    });
                    return newAppId;
                }
                return null;
            } catch (error) {
                console.error('Failed to create application:', error);
                set({ autoSaveStatus: 'error' });
                return null;
            }
        },

        /**
         * Update application locally and trigger auto-save
         */
        updateApplication: (data) => {
            set((state) => {
                // Strip null/undefined values so we never overwrite valid store
                // data with nulls coming from partial API responses.
                const safeData = Object.fromEntries(
                    Object.entries(data).filter(([_, v]) => v !== null && v !== undefined)
                ) as Partial<ApplicationData>;
                return {
                    application: {
                        ...state.application,
                        ...safeData,
                        updatedAt: new Date().toISOString(),
                    },
                };
            });

            // Auto-save: trigger debounced save on store updates — but only
            // after hydration, to prevent sending undefined fields over real data.
            const { applicationId } = get().application;
            if (applicationId && get().isInitialized) {
                debouncedSave();
            }
        },

        mergeApplicationFromServer: (data) => {
            set((state) => ({
                application: {
                    ...state.application,
                    // Spread all fields including nulls — caller is responsible for passing
                    // a complete server response so nulls represent genuine cleared values.
                    ...(data as Partial<ApplicationData>),
                    updatedAt: new Date().toISOString(),
                },
            }));
        },

        /**
         * Save to server immediately
         */
        saveToServer: async () => {
            const { application } = get();

            if (!application.applicationId) {
                console.warn('[DELEG-DIAG] saveToServer: ⚠️ No applicationId — skipping auto-save');
                return;
            }

            // CRITICAL GUARD: Never auto-save before the store is hydrated from the
            // server (loadApplicationById). Without this, the debounced save fires with
            // undefined fields from the initial empty store and OVERWRITES real data on
            // the server. This was the root cause of delegated edits being wiped: the
            // owner's login triggered ApplicationLayout's step-sync → debouncedSave
            // before the GET returned, sending customer_type=undefined → server wrote
            // null over the helper's NATIONAL_CITIZEN.
            if (!get().isInitialized) {
                console.warn('[DELEG-DIAG] saveToServer: ⚠️ Store not initialized yet — skipping auto-save to prevent overwriting server data');
                return;
            }

            console.log(`[DELEG-DIAG] saveToServer: auto-saving appId=${application.applicationId}  customerType=${application.customerType}  productType=${application.productType}`);

            try {
                set({ autoSaveStatus: 'saving' });

                // Build API payload with flat camelCase fields.
                // Only include fields that have actual values — no placeholders.
                // CRITICAL: customer_type and product_type are conditional — if the
                // store hasn't been hydrated yet (or hydration missed them), sending
                // undefined/null would cause the backend to preserve existing data
                // (the `if != null` check), but we should never send classification
                // fields unless we have real values. This prevents any edge case
                // where a serializer, proxy, or future backend change treats absent
                // differently from null.
                const apiData: Record<string, unknown> = {
                    incomeCurrency: application.incomeCurrency || 'DOP',
                };
                if (application.customerType) apiData.customer_type = application.customerType;
                if (application.productType)  apiData.product_type = application.productType;
                if (application.currentStep && application.currentStep > 1) apiData.currentStep = application.currentStep;

                // Personal info — only send fields with real values
                if (application.firstName)          apiData.firstName = application.firstName;
                if (application.lastName)            apiData.lastName = application.lastName;
                if (application.email)               apiData.email = application.email;
                if (application.phone)               apiData.phoneNumber = application.phone;
                if (application.dateOfBirth)          apiData.dateOfBirth = application.dateOfBirth;
                if (application.nationality)          apiData.nationality = application.nationality;
                if (application.countryOfResidence)   apiData.countryOfResidence = application.countryOfResidence;
                if (application.idNumber)             apiData.passportNumber = application.idNumber;
                if (application.currentAddress)       apiData.currentAddress = application.currentAddress;
                if (application.city)                 apiData.city = application.city;
                if (application.stateProvince)        apiData.stateProvince = application.stateProvince;
                if (application.postalCode)           apiData.postalCode = application.postalCode;
                if (application.maritalStatus)        apiData.maritalStatus = application.maritalStatus;
                if (application.numberOfDependents != null) apiData.numberOfDependents = application.numberOfDependents;
                if (application.country)              apiData.country = application.country;

                // Financial info
                if (application.employmentStatus)     apiData.employmentStatus = application.employmentStatus;
                if (application.employerName)          apiData.employerName = application.employerName;
                if (application.jobTitle)              apiData.jobTitle = application.jobTitle;
                if (application.yearsEmployed != null) apiData.yearsEmployed = application.yearsEmployed;
                if (application.employmentStartDate)   apiData.employmentStartDate = application.employmentStartDate;
                if (application.monthlyIncome)         apiData.monthlyIncome = application.monthlyIncome;
                if (application.annualIncome)           apiData.annualIncome = application.annualIncome;
                if (application.otherIncome)            apiData.otherIncome = application.otherIncome;
                if (application.monthlyExpenses)        apiData.monthlyDebts = application.monthlyExpenses;
                if (application.existingDebts)          apiData.totalLiabilities = application.existingDebts;
                if (application.totalAssets)             apiData.totalAssets = application.totalAssets;

                // Property info
                if (application.propertyAddress)       apiData.propertyAddress = application.propertyAddress;
                if (application.propertyType)          apiData.propertyType = application.propertyType;
                if (application.propertyCondition)     apiData.propertyCondition = application.propertyCondition;
                if (application.loanPurpose)           apiData.loanPurpose = application.loanPurpose;
                if (application.propertyValue)         apiData.propertyValue = application.propertyValue;
                if (application.loanAmount)            apiData.loanAmount = application.loanAmount;
                if (application.downPayment)           apiData.downPayment = application.downPayment;
                if (application.loanTerm)              apiData.loanTerm = application.loanTerm;
                if (application.province)              apiData.province = application.province;
                if (application.municipality)          apiData.municipality = application.municipality;
                if (application.ltv)                   apiData.ltv = application.ltv;
                if (application.exchangeRateToDOP)     apiData.exchangeRateToDOP = application.exchangeRateToDOP;

                const response = await applicationService.updateApplication(
                    application.applicationId,
                    apiData
                );

                if (response.success) {
                    set({ autoSaveStatus: 'saved' });

                    // Reset to idle after 2 seconds
                    setTimeout(() => {
                        if (get().autoSaveStatus === 'saved') {
                            set({ autoSaveStatus: 'idle' });
                        }
                    }, 2000);
                } else {
                    set({ autoSaveStatus: 'error' });
                }
            } catch (error) {
                console.error('Failed to save application:', error);
                set({ autoSaveStatus: 'error' });
            }
        },

        setCurrentStep: (step) => {
            set((state) => ({
                application: {
                    ...state.application,
                    currentStep: Math.max(state.application.currentStep || 1, step),
                },
                // Reset stale save status on step change — prevents errors from
                // a previous step (or from loadApplicationById's status reuse)
                // from bleeding into the new step's UI.
                autoSaveStatus: 'idle',
            }));
            // Persist the step watermark to the server (lightweight — only sends currentStep).
            // This survives browser closes and session switches. The full application
            // save is NOT triggered here to avoid the delegation field tracker issue.
            const { applicationId } = get().application;
            const newStep = Math.max(get().application.currentStep || 1, step);
            if (applicationId && applicationId !== 'new' && get().isInitialized) {
                applicationService.updateApplication(applicationId, { currentStep: newStep } as any)
                    .catch(() => { /* best-effort — field heuristics are the fallback */ });
            }
        },

        setAutoSaveStatus: (status) => set({ autoSaveStatus: status }),

        resetApplication: () => set({
            application: initialApplication,
            autoSaveStatus: 'idle',
            isInitialized: false,
        }),

        // Helper methods for status checks
        canEditApplication: () => {
            const { status } = get().application;
            // Can only edit if status is DRAFT
            return status === 'DRAFT';
        },

        canSubmitSecure: () => {
            const { status } = get().application;
            // Can submit secure data if payment is confirmed or ready for submission
            return status === 'PAYMENT_CONFIRMED' || status === 'READY_FOR_SUBMISSION';
        },

        isSubmitted: () => {
            const { status } = get().application;
            // Application is submitted
            return status === 'SUBMITTED';
        },

        // Bank Account Actions
        loadBankAccounts: async () => {
            const { applicationId } = get().application;
            if (!applicationId) {
                console.warn('Cannot load bank accounts: No application ID');
                return;
            }

            try {
                const accounts = await bankAccountService.listBankAccounts(applicationId);
                set((state) => ({
                    application: {
                        ...state.application,
                        bankAccounts: accounts,
                    },
                }));
            } catch (error) {
                console.error('Failed to load bank accounts:', error);
            }
        },

        addBankAccount: (account) => {
            set((state) => ({
                application: {
                    ...state.application,
                    bankAccounts: [...(state.application.bankAccounts || []), account],
                },
            }));
        },

        updateBankAccount: (accountId, account) => {
            set((state) => ({
                application: {
                    ...state.application,
                    bankAccounts: (state.application.bankAccounts || []).map((acc) =>
                        acc.id === accountId ? account : acc
                    ),
                },
            }));
        },

        removeBankAccount: (accountId) => {
            set((state) => ({
                application: {
                    ...state.application,
                    bankAccounts: (state.application.bankAccounts || []).filter(
                        (acc) => acc.id !== accountId
                    ),
                },
            }));
        },
    };
});
// ── Simulator Prefill ──────────────────────────────────────────────────────────
// Written by PublicSimulatorPage when the user starts a formal application.
// Read once by ApplicationLayout on /application/new, then cleared.
// All monetary values are stored in DOP (STORAGE_CURRENCY).

export const SIM_PREFILL_KEY = 'hipotech_sim_prefill_v1';

export interface SimPrefill {
    savedAt?: string;
    // Step 1 / 2
    customerType?: CustomerType;
    country?: string;
    productType?: ProductType;
    // Step 3
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    maritalStatus?: string;
    countryOfResidence?: string;
    // Step 4 — all monetary in DOP
    employmentStatus?: string;
    monthsAtCurrentEmployer?: number;
    annualIncome?: number;    // DOP
    monthlyIncome?: number;   // DOP
    totalAssets?: number;     // DOP
    existingDebts?: number;   // DOP
    incomeCurrency?: string;
    // Step 5 — all monetary in DOP
    propertyType?: string;
    propertyCondition?: string;
    propertyValue?: number;   // DOP
    downPayment?: number;     // DOP
    loanAmount?: number;      // DOP
    loanTerm?: number;
    occupancyType?: string;
    // Credit
    creditScore?: number;
    creditScoreUsaEquivalent?: number;
}


export function writeSimPrefill(data: SimPrefill): void {
    try {
        localStorage.setItem(SIM_PREFILL_KEY, JSON.stringify({
            ...data,
            savedAt: new Date().toISOString(),
        }));
    } catch { /* localStorage unavailable */ }
}

export function readSimPrefill(): SimPrefill | null {
    try {
        const raw = localStorage.getItem(SIM_PREFILL_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as SimPrefill;
        // Expire after 24 hours — same TTL as WizardDraft
        if (parsed.savedAt) {
            const age = Date.now() - new Date(parsed.savedAt).getTime();
            if (age > 24 * 60 * 60 * 1000) { clearSimPrefill(); return null; }
        }
        return parsed;
    } catch { return null; }
}

export function clearSimPrefill(): void {
    try { localStorage.removeItem(SIM_PREFILL_KEY); } catch { /* ignore */ }
}